(() => {
  if (window.ProductMain) return;
  const formatMoney = (cents, format) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    }).format(cents / 100);
  };

  const initProductSection = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const media = [...root.querySelectorAll('[data-product-media]')];
    const thumbs = [...root.querySelectorAll('[data-product-thumb]')];
    const mediaTrack = root.querySelector('[data-media-track]');
    let currentMedia = 0;
    const showMedia = (index) => {
      if (!media.length || !mediaTrack) return;

      currentMedia =
          (index + media.length) % media.length;

      mediaTrack.style.transform =
          `translate3d(-${currentMedia * 100}%, 0, 0)`;

      media.forEach((media, mediaIndex) => {
        const isActive = mediaIndex === currentMedia;

        media.classList.toggle('is-active', isActive);
        media.setAttribute(
            'aria-hidden',
            isActive ? 'false' : 'true'
        );
      });

      thumbs.forEach((thumb) => {
        const isActive = Number(thumb.dataset.index) === currentMedia;

        thumb.classList.toggle('is-active', isActive);

        thumb.setAttribute(
            'aria-current',
            isActive ? 'true' : 'false'
        );
      });
    };
    root.querySelector('[data-media-prev]')?.addEventListener('click', () => showMedia(currentMedia - 1));
    root.querySelector('[data-media-next]')?.addEventListener('click', () => showMedia(currentMedia + 1));
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showMedia(Number(thumb.dataset.index))));

    let touchStartX = 0;
    const mediaStage = root.querySelector('.product-main__media-stage');
    mediaStage?.addEventListener('touchstart', (event) => { touchStartX = event.touches[0]?.clientX || 0; }, { passive: true });
    mediaStage?.addEventListener('touchend', (event) => {
      const distance = (event.changedTouches[0]?.clientX || 0) - touchStartX;
      if (Math.abs(distance) > 50) showMedia(currentMedia + (distance < 0 ? 1 : -1));
    }, { passive: true });

    const zoomDialog = root.querySelector('[data-media-zoom-dialog]');
    const zoomContent = root.querySelector('[data-media-zoom-content]');
    const zoomThumbs = [...root.querySelectorAll('[data-zoom-thumbnail]')];
    const zoomIndexes = zoomThumbs.map((thumb) => Number(thumb.dataset.index));
    const zoomPreloads = new Map();
    let zoomRequest = 0;
    let currentZoomIndex = 0;
    zoomThumbs.forEach((thumb) => {
      const preload = new Image();
      preload.src = thumb.dataset.zoomSrc;
      preload.alt = thumb.dataset.zoomAlt || '';
      const ready = preload.decode
        ? preload.decode().catch(() => new Promise((resolve) => {
          if (preload.complete) resolve();
          else preload.addEventListener('load', resolve, { once: true });
        }))
        : new Promise((resolve) => {
          if (preload.complete) resolve();
          else preload.addEventListener('load', resolve, { once: true });
        });
      zoomPreloads.set(Number(thumb.dataset.index), { image: preload, ready });
    });
    const showZoomImage = async (index, fallbackImage, direction = 0) => {
      if (!zoomContent) return;
      const request = ++zoomRequest;
      const thumb = zoomThumbs.find((item) => Number(item.dataset.index) === index);
      currentZoomIndex = index;
      zoomThumbs.forEach((item) => item.classList.toggle('is-active', Number(item.dataset.index) === index));
      thumb?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      zoomContent.classList.add('is-loading');
      if (!zoomContent.firstElementChild && fallbackImage) {
        const initialImage = fallbackImage.cloneNode();
        initialImage.classList.add('product-main__zoom-image');
        zoomContent.replaceChildren(initialImage);
      }

      const preloaded = zoomPreloads.get(index);
      if (preloaded) {
        await preloaded.ready;
        if (request !== zoomRequest) return;
        const nextImage = preloaded.image.cloneNode();
        nextImage.className = 'product-main__zoom-image';
        const currentImage = zoomContent.querySelector('.product-main__zoom-image');
        if (currentImage && direction !== 0) {
          nextImage.classList.add(direction > 0 ? 'is-entering-right' : 'is-entering-left');
          zoomContent.append(nextImage);
          nextImage.getBoundingClientRect();
          currentImage.classList.add(direction > 0 ? 'is-leaving-left' : 'is-leaving-right');
          nextImage.classList.add('is-entered');
          await new Promise((resolve) => {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
              requestAnimationFrame(resolve);
              return;
            }
            const fallback = window.setTimeout(resolve, 550);
            const onTransitionEnd = (event) => {
              if (event.propertyName !== 'transform') return;
              window.clearTimeout(fallback);
              nextImage.removeEventListener('transitionend', onTransitionEnd);
              resolve();
            };
            nextImage.addEventListener('transitionend', onTransitionEnd);
          });
          if (request !== zoomRequest) return;
          nextImage.style.transition = 'none';
          nextImage.className = 'product-main__zoom-image';
          zoomContent.replaceChildren(nextImage);
          nextImage.getBoundingClientRect();
          nextImage.style.removeProperty('transition');
        } else {
          zoomContent.replaceChildren(nextImage);
        }
      } else if (fallbackImage) {
        const zoomImage = fallbackImage.cloneNode();
        zoomImage.classList.add('product-main__zoom-image');
        zoomContent.replaceChildren(zoomImage);
      }
      zoomContent.classList.remove('is-loading');
    };
    root.querySelector('[data-media-zoom]')?.addEventListener('click', () => {
      const activeImage = media[currentMedia]?.querySelector('img');
      if (!zoomDialog || !zoomContent || !activeImage) return;
      showZoomImage(currentMedia, activeImage);
      zoomDialog.classList.add('is-opening');
      zoomDialog.showModal();
      requestAnimationFrame(() => requestAnimationFrame(() => zoomDialog.classList.remove('is-opening')));
    });
    const navigateZoom = (step) => {
      const position = zoomIndexes.indexOf(currentZoomIndex);
      const nextPosition = (position + step + zoomIndexes.length) % zoomIndexes.length;
      showZoomImage(zoomIndexes[nextPosition], null, step);
    };
    zoomThumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      const index = Number(thumb.dataset.index);
      const direction = zoomIndexes.indexOf(index) > zoomIndexes.indexOf(currentZoomIndex) ? 1 : -1;
      if (index !== currentZoomIndex) showZoomImage(index, null, direction);
    }));
    root.querySelector('[data-zoom-prev]')?.addEventListener('click', () => navigateZoom(-1));
    root.querySelector('[data-zoom-next]')?.addEventListener('click', () => navigateZoom(1));
    zoomDialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') navigateZoom(-1);
      if (event.key === 'ArrowRight') navigateZoom(1);
    });
    root.querySelector('[data-media-zoom-close]')?.addEventListener('click', () => zoomDialog?.close());
    zoomDialog?.addEventListener('click', (event) => { if (event.target === zoomDialog) zoomDialog.close(); });

    const quantity = root.querySelector('[data-qty-input]');
    const mobileQuantity = root.querySelector('[data-mobile-qty-input]');
    const setQuantity = (value) => {
      const next = Math.max(1, Math.floor(Number(value) || 1));
      if (quantity) quantity.value = next;
      if (mobileQuantity) mobileQuantity.value = next;
    };
    root.querySelector('[data-qty-minus]')?.addEventListener('click', () => setQuantity(Number(quantity.value || 1) - 1));
    root.querySelector('[data-qty-plus]')?.addEventListener('click', () => setQuantity(Number(quantity.value || 1) + 1));
    root.querySelector('[data-mobile-qty-minus]')?.addEventListener('click', () => setQuantity(Number(mobileQuantity.value || 1) - 1));
    root.querySelector('[data-mobile-qty-plus]')?.addEventListener('click', () => setQuantity(Number(mobileQuantity.value || 1) + 1));
    root.querySelector('[data-sample-link]')?.addEventListener('click', (event) => {
      const link = event.currentTarget;
      const variantId = root.querySelector('[data-variant-id]')?.value;
      if (!variantId) return;
      const url = new URL(link.href);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    });
    quantity?.addEventListener('input', () => { if (quantity.value) setQuantity(quantity.value); });
    mobileQuantity?.addEventListener('input', () => { if (mobileQuantity.value) setQuantity(mobileQuantity.value); });
    quantity?.addEventListener('change', () => setQuantity(quantity.value));
    mobileQuantity?.addEventListener('change', () => setQuantity(mobileQuantity.value));

    const variantsElement = root.querySelector('[data-product-variants]');
    const optionInputs = [...root.querySelectorAll('[data-product-option]')];
    if (!variantsElement || !optionInputs.length) return;

    let variants = [];
    try { variants = JSON.parse(variantsElement.textContent); } catch (error) { return; }

    const updateOptionAvailability = (selectedOptions) => {
      root.querySelectorAll('[data-option-index]').forEach((group, optionIndex) => {
        group.querySelectorAll('[data-product-option]').forEach((input) => {
          const candidateOptions = [...selectedOptions];
          candidateOptions[optionIndex] = input.value;
          const possible = variants.some((item) => item.available && item.options.every((option, index) => option === candidateOptions[index]));
          input.disabled = !possible;
          input.closest('.product-main__option-value')?.classList.toggle('is-unavailable', !possible);
        });
      });
    };

    const updateVariant = () => {
      const selectedOptions = [...root.querySelectorAll('[data-option-index]')].map((group) => {
        const checked = group.querySelector('[data-product-option]:checked');
        const selectedLabel = group.querySelector('[data-selected-option]');
        if (selectedLabel) selectedLabel.textContent = checked?.value || '';
        return checked?.value;
      });
      updateOptionAvailability(selectedOptions);
      const variant = variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));
      if (!variant) return;

      root.querySelector('[data-variant-id]').value = variant.id;
      root.querySelector('[data-product-price]').textContent = formatMoney(variant.price);
      const comparePrice = root.querySelector('[data-product-compare-price]');
      if (comparePrice) {
        comparePrice.hidden = !(variant.compare_at_price > variant.price);
        comparePrice.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price) : '';
      }

      const buy = root.querySelector('.product-main__buy');
      root.querySelectorAll('[data-add-button], [data-add-button-mobile]').forEach((addButton) => {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? buy.dataset.addLabel : (buy.dataset.soldOutLabel || 'Sold out');
      });
      const stock = root.querySelector('.product-main__stock');
      const stockLabel = root.querySelector('[data-stock-label]');
      const stockTrack = root.querySelector('[data-stock-track]');
      if (stock && stockLabel) {
        stockLabel.textContent = variant.available ? stock.dataset.inStockText : stock.dataset.outOfStockText;
        stockLabel.classList.toggle('product-main__stock-label--out', !variant.available);
        if (stockTrack) stockTrack.hidden = !variant.available;
      }
      if (root.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };
    optionInputs.forEach((input) => input.addEventListener('change', updateVariant));
    updateVariant();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-product-section]').forEach(initProductSection);
  window.ProductMain = { initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
